import { css, SerializedStyles } from '@emotion/react';
import React from 'react';
import { EmotionTheme } from 'src/domains/layouts/config/emotionTheme';
import { ChevronIcon } from 'src/ui/assets/icons/arrow/ChevronIcon';
import { LoadingIcon } from 'src/ui/assets/icons/other/LoadingIcon';
import { SearchIcon } from 'src/ui/assets/icons/other/SearchIcon';
import { ButtonAppearanceType, ButtonIconOnlyType, ButtonSizeType } from 'src/ui/components/button/icon-only/types';

export const renderIcon = (isLoading: boolean, icon?: ButtonIconOnlyType): JSX.Element | null => {
    if (isLoading) {
        return <LoadingIcon />;
    }

    if (icon === undefined) {
        return null;
    }

    switch (icon) {
        case 'search-icon':
            return <SearchIcon />;
        case 'chevron-left':
            return <ChevronIcon position='left' />;
        default:
            return null;
    }
};

export const buildBtnDisabledOrLoading = (disabled?: boolean, isLoading?: boolean): SerializedStyles | null => {
    if (disabled === true) {
        return css`
            cursor: not-allowed;
            opacity: 0.4;
            pointer-events: none;
        `;
    }

    if (isLoading === true) {
        return css`
            pointer-events: none;
        `;
    }

    return null;
};

export const appearanceStyle = (theme: EmotionTheme, appearance: ButtonAppearanceType): SerializedStyles =>
    ({
        primary: css`
            background: ${theme.star.button.primary.colors.bgPrimary};
            color: ${theme.star.button.primary.colors.txtPrimary};
            &:hover,
            &:active {
                &::after {
                    background: ${theme.star.button.primary.colors.bgSecondary};
                }
            }
        `,
        secondary: css`
            background: ${theme.star.button.secondary.colors.bgPrimary};
            color: ${theme.star.button.secondary.colors.txtPrimary};
            &:hover,
            &:active {
                &::after {
                    background: ${theme.star.button.secondary.colors.bgSecondary};
                }
            }
        `,
        'secondary-dark': css`
            background: ${theme.star.button.secondaryDark.colors.bgPrimary};
            color: ${theme.star.button.secondaryDark.colors.txtPrimary};
            &:hover,
            &:active {
                &::after {
                    background: ${theme.star.button.secondaryDark.colors.bgSecondary};
                }
            }
        `,
        danger: css`
            background: ${theme.star.button.danger.colors.bgPrimary};
            color: ${theme.star.button.danger.colors.txtPrimary};
            &:hover,
            &:active {
                &::after {
                    background: ${theme.star.button.danger.colors.bgSecondary};
                }
            }
        `,
    })[appearance];

export const sizeStyles = (theme: EmotionTheme, size: ButtonSizeType): SerializedStyles =>
    ({
        'x-small': css`
            border-radius: ${theme.star.button.common.radiusPrimary};
            flex: 0 0 24px;
            font-size: ${theme.star.button.common.typography.fontSizePrimary};
            height: 24px;
            line-height: ${theme.star.button.common.typography.lineHeightPrimary};
            padding: 4px;
            width: 24px;
        `,
        small: css`
            border-radius: ${theme.star.button.common.radiusSecondary};
            flex: 0 0 32px;
            font-size: ${theme.star.button.common.typography.fontSizePrimary};
            height: 32px;
            line-height: ${theme.star.button.common.typography.lineHeightPrimary};
            padding: 8px;
            width: 32px;
        `,
        medium: css`
            border-radius: ${theme.star.button.common.radiusSecondary};
            flex: 0 0 44px;
            font-size: ${theme.star.button.common.typography.fontSizeSecondary};
            height: 44px;
            line-height: ${theme.star.button.common.typography.lineHeightSecondary};
            padding: 12px;
            width: 44px;
        `,
        large: css`
            border-radius: ${theme.star.button.common.radiusSecondary};
            flex: 0 0 56px;
            font-size: ${theme.star.button.common.typography.fontSizeTertiary};
            height: 56px;
            line-height: ${theme.star.button.common.typography.lineHeightSecondary};
            padding: 16px;
            width: 56px;
        `,
    })[size];
